import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Hammer from 'react-hammerjs';
import { calculateRem } from '@uie/javascript-style-helpers';
import { breakpoints } from '@vzrf/react-theme-helpers';

import FilterChipScrollButton from './FilterChipScrollButton';
import FilterScrollContainer from './FilterScrollContainer';
import FilterChip from './FilterChip';

const FilterChipControlsStyled = styled.div`
  display: flex;
  flex: 2;
  flex-direction: row;
  height: ${props => calculateRem(props.filterStyles.height)};
  @media screen and (min-width: ${breakpoints.sm}) {
    flex-direction: row;
    justify-content: space-between;
    white-space: nowrap;
  }
`;
const FilterSearchChipStyled = styled.div`
  padding-top: 2px;
  overflow-y: hidden;
  overflow-x: hidden;
  min-width: 5rem;
`;

class FilterChipControls extends React.PureComponent {
  static propTypes = {
    chipAddedFadeInDuration: PropTypes.number,
    closeChipLabel: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.element,
    ]),
    filters: PropTypes.arrayOf(PropTypes.object).isRequired,
    filterChipStyles: PropTypes.object,
    filterStyles: PropTypes.object,
    handleChipClick: PropTypes.func,
    leftScrollStyles: PropTypes.object,
    rightScrollStyles: PropTypes.object,
    resetScroll: PropTypes.bool,
    scrollDuration: PropTypes.number
  };

  static defaultProps = {
    chipAddedFadeInDuration: null,
    closeChipLabel: undefined,
    filterChipStyles: {},
    filterStyles: {},
    handleChipClick: undefined,
    leftScrollStyles: {},
    rightScrollStyles: {},
    resetScroll: false,
    scrollDuration: null
  };

  constructor(props) {
    super(props);

    this.resetScroll = false;
    this.updatingScrolls = false;
    this.chipContainerSize = null;
    this.chipContainerWrapperSize = null;

    this.state = {
      chipScrollOffset: 0,
      showLeftScroll: false,
      showRightScroll: false,
      filterChipOffsetValue: undefined
    };
  }

  componentDidMount() {
    this.updatingScrolls = false;
    window.addEventListener('resize', this.updateSize);
    this.updateSize();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resetScroll !== this.props.resetScroll ||
      prevProps.filters.length !== this.props.filters.length
    ) {
      this.resetScroll = this.props.resetScroll;
      this.updateSize();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateSize);
  }

  getWrapperComponent = element => {
    this.chipContainerWrapper = element;
    this.chipContainerWrapperSize = element.getBoundingClientRect();
  };

  getContainerComponent = element => {
    this.chipContainer = element;
    this.chipContainerSize = element.getBoundingClientRect();
    let offsetValues = {}
    let nodes =  [...element.childNodes];
    let offset = 0;
    if(this.state.filterChipOffsetValue === undefined || (this.state.filterChipOffsetValue !== undefined && this.state.filterChipOffsetValue !== null && Object.entries(this.state.filterChipOffsetValue).length === 0)) {
      nodes.forEach((child, index)=>{
        offsetValues[child.id] = offset;
        offset += child.getBoundingClientRect().width;
      })
      console.log(offsetValues);
      this.setState({filterChipOffsetValue:offsetValues});
    }
  };

  chipClicked = (id, rank, e) => {

      // Notify Application that the Filter Chip was Clicked
      if (this.props.handleChipClick) {
        this.props.handleChipClick(id, rank, e);
      }
        const chipScrollOffset = this.state.filterChipOffsetValue !== undefined && this.state.filterChipOffsetValue !== null  && Object.entries(this.state.filterChipOffsetValue).length !== 0 ? this.state.filterChipOffsetValue[id] * -1 : this.state.chipScrollOffset;
        this.updateScrollOffset(chipScrollOffset);
  };

  updateScrollOffset = scrollOffset => {
    let chipScrollOffset = scrollOffset;

    // Check if ResultsFilter told us to clear any Scroll Offset
    if (this.resetScroll) {
      chipScrollOffset = 0;
      this.resetScroll = false;
    }

    // Handle Offset when Container smaller than Wrapper
    if (this.chipContainerSize.width < this.chipContainerWrapperSize.width) {
      if (this.state.chipScrollOffset !== 0) {
        chipScrollOffset = 0;
      }
    }

    // Check for Offset past Beginning
    if (chipScrollOffset > 0) {
      chipScrollOffset = 0;
    }

    // Check for Offset past the End
    const maxScrollOffset =
      (this.chipContainerSize.width -
        this.chipContainerWrapperSize.width * 0.25) *
      -1;
    if (chipScrollOffset < 0 && chipScrollOffset < maxScrollOffset) {
      chipScrollOffset = maxScrollOffset;
    }

    // Set the new Scroll Offset, and update the Show Scroll Buttons
    if (chipScrollOffset !== this.state.chipScrollOffset) {
      this.setState({ chipScrollOffset }, () => this.updateShowScrolls());
    } else {
      this.updateShowScrolls();
    }
  };

  handleScroll = (direction, event) => {
    event.preventDefault();

    if (!this.chipContainerSize || !this.chipContainerWrapperSize) {
      return;
    }

    // Check if Scroll was actually a Swipe
    let scrollDirection = direction;
    if (direction === 'swipe') {
      // Check for Diagonal or Vertical Swipes, and ignore them
      if (event.offsetDirection !== 2 && event.offsetDirection !== 4) {
        return;
      }

      // Check for Swipe Left when Chip Container smaller than Chip Wrapper
      if (
        this.chipContainerSize.width <= this.chipContainerWrapperSize.width &&
        this.state.chipScrollOffset === 0
      ) {
        return;
      }

      scrollDirection = event.offsetDirection === 4 ? 'left' : 'right';
    }

    // Calculate Scroll Distance
    const addOffset = this.chipContainerWrapperSize.width * 0.75;
    let chipScrollOffset =
      this.state.chipScrollOffset +
      (scrollDirection === 'right' ? -addOffset : addOffset);

    this.updateScrollOffset(chipScrollOffset);
  };

  updateSize = () => {
    this.chipContainerSize = this.chipContainer.getBoundingClientRect();
    this.chipContainerWrapperSize = this.chipContainerWrapper.getBoundingClientRect();

    this.updateScrollOffset(this.state.chipScrollOffset);
  };

  updateShowScrolls = () => {
    if (this.updatingScrolls) {
      return -1;
    }

    this.updatingScrolls = true;

    // Check if we have both container sizes, if not we can't do anything
    if (!this.chipContainerSize || !this.chipContainerWrapperSize) {
      this.updatingScrolls = false;
      return -2;
    }

    // Check if Container is larger than Wrapper, if not, remove both Scrolls
    if (this.chipContainerSize.width < this.chipContainerWrapperSize.width) {
      this.setState({
        showLeftScroll: false,
        showRightScroll: false,
      });

      this.updatingScrolls = false;
      return 0;
    }

    // If in Mobile Mode (or Viewport just too small), remove both Scrolls
    if (
      (window.innerWidth > window.innerHeight &&
        window.innerWidth < parseInt(breakpoints.sm, 10)) ||
      (window.innerHeight > window.innerHeight &&
        window.innerHeight < parseInt(breakpoints.sm, 10))
    ) {
      this.setState({
        showLeftScroll: false,
        showRightScroll: false,
      });

      this.updatingScrolls = false;
      return 0;
    }

    // Determine which Scrolls to show
    const showLeftScroll = this.state.chipScrollOffset < 0;
    const showRightScroll =
      this.chipContainerSize.width + this.state.chipScrollOffset >
      this.chipContainerWrapperSize.width;

    // Check if any Show Scroll actually changed
    if (
      showLeftScroll === this.state.showLeftScroll &&
      showRightScroll === this.state.showRightScroll
    ) {
      this.updatingScrolls = false;

      return showLeftScroll + showRightScroll;
    }

    // Update Scrolls
    this.setState({
      showLeftScroll,
      showRightScroll,
    });

    this.updatingScrolls = false;

    return showLeftScroll + showRightScroll;
  };

  renderSearchFilter = () => {
    const filter = this.props.filters.filter(filter => filter.id === 'search').map(filter => {
      const { id, styles, title, isSelected } = filter;
      return (
        <FilterChip
          selectedCallback={this.props.handleChipClick}
          filterChipStyles={this.props.filterChipStyles}
          id={id}
          key={id}
          styles={styles}
          title={title}
          isSelected={isSelected}
          filterStyles={this.props.filterStyles}
        />
      );
    });

    return filter;
  };

  render() {
    return (
      <Hammer onPanStart={e => this.handleScroll('swipe', e)}>
        <FilterChipControlsStyled filterStyles={this.props.filterStyles}>
        <FilterSearchChipStyled>
        {this.renderSearchFilter()}
        </FilterSearchChipStyled>
          <FilterChipScrollButton
            direction="left"
            handleScroll={this.handleScroll}
            scrollStyles={this.props.leftScrollStyles}
            show={this.state.showLeftScroll}
          />
          <FilterScrollContainer
            chipAddedFadeInDuration={this.props.chipAddedFadeInDuration}
            chipScrollOffset={this.state.chipScrollOffset}
            closeChipLabel={this.props.closeChipLabel}
            filters={this.props.filters}
            filterChipStyles={this.props.filterChipStyles}
            filterStyles={this.props.filterStyles}
            getContainerComponent={this.getContainerComponent}
            getWrapperComponent={this.getWrapperComponent}
            handleChipClick={this.chipClicked}
            scrollDuration={this.props.scrollDuration}
            showLeftScroll={this.state.showLeftScroll}
            showRightScroll={this.state.showRightScroll}
          />
          <FilterChipScrollButton
            direction="right"
            handleScroll={this.handleScroll}
            scrollStyles={this.props.rightScrollStyles}
            show={this.state.showRightScroll}
          />
        </FilterChipControlsStyled>
      </Hammer>
    );
  }
}

export default FilterChipControls;
